// Lightweight i18n — English + Spanish. Language choice persists in localStorage
// (`dwj_lang`, non-sensitive). Full deep translation of admin/employer flows is
// English-only for now; this covers the primary job-seeker surface.
import { useEffect, useState } from "react";

const STORAGE_KEY = "dwj_lang";
const SUPPORTED = ["en", "es"];
const DEFAULT = "en";

const DICT = {
    en: {
        // Header
        "search.placeholder": "Search city, ZIP, job…",
        "search.no_matches": "No matches. Try a different city or job title.",
        "search.clear": "clear",
        "search.near_me": "Near me",
        "search.for_employers": "For employers",
        "search.sign_in": "Sign in",
        "brand.tagline": "Daily-wage jobs across the USA & Canada — one tap away 📍",
        "menu.admin_panel": "Admin panel",
        "menu.my_apps": "My applications",
        "menu.job_alerts": "Job alerts",
        "menu.saved": "Saved jobs",
        "menu.employer_portal": "Employer portal",
        "menu.logout": "Log out",
        // Filter chips
        "chip.all": "All jobs",
        "cat.restaurant": "Restaurant",
        "cat.retail": "Retail",
        "cat.warehouse": "Warehouse",
        "cat.delivery": "Delivery",
        "cat.hotels": "Hotels",
        "cat.care": "Care",
        "cat.cleaning": "Cleaning",
        "cat.security": "Security",
        "cat.events": "Events",
        // Loading / status
        "loading.jobs": "Loading live jobs",
        "loading.subline": "Fetching the latest openings across USA & Canada…",
        "loading.pill": "Loading jobs…",
        "loading.tip": "Tip: tap any pin to apply in one click — no signup needed 👋",
        "loading.retry": "Couldn't reach the live feed. Showing a small sample — try refreshing.",
        // Onboarding cards
        "ob.card1.eyebrow": "Welcome to Dailyworkjobs",
        "ob.card1.title": "Real hourly jobs — pinned on a map",
        "ob.card1.body": "Thousands of daily-wage roles across the USA and Canada, all in one place. No signup, no resume walls. Just tap and apply.",
        "ob.card1.cta": "Show me around",
        "ob.card2.eyebrow": "How it works",
        "ob.card2.title": "Browse → tap a pin → apply",
        "ob.card2.body": "Each pin is a real hiring employer near you. Tap one to see pay & shift, fill in your name/phone/email once, and we redirect you straight to their real application page.",
        "ob.card2.cta": "Got it",
        "ob.card2.step1": "1. Explore the map",
        "ob.card2.step2": "2. Tap any pin",
        "ob.card2.step3": "3. One-tap apply",
        "ob.card3.eyebrow": "Employers welcome too",
        "ob.card3.title": "Post jobs. Get alerts. Save favorites.",
        "ob.card3.body": "Employers can post directly through the Employer Portal (★ pins highlight verified employers). Jobseekers get daily email alerts and can save jobs to apply later.",
        "ob.card3.cta": "Almost there",
        "ob.card3.employers": "Employers",
        "ob.card3.jobseekers": "Jobseekers",
        "ob.card3.emp_perks": "Post jobs · Get verified · ★ Featured",
        "ob.card3.js_perks": "Daily alerts · Save jobs · Track apps",
        "ob.card4.eyebrow": "You're all set",
        "ob.card4.title": "Let's find your next shift",
        "ob.card4.body": "The map is warmed up and full of live jobs. Zoom into your city, filter by category, and apply to as many roles as you like — it's completely free.",
        "ob.card4.cta": "Let's start",
        "ob.card4.strap": "Free · No signup · No resume required",
        "ob.progress": "of",
        // Language toggle itself
        "lang.toggle.title": "Cambiar idioma / Change language",
    },
    es: {
        // Header
        "search.placeholder": "Buscar ciudad, código postal, empleo…",
        "search.no_matches": "Sin resultados. Prueba con otra ciudad o puesto.",
        "search.clear": "borrar",
        "search.near_me": "Cerca de mí",
        "search.for_employers": "Empleadores",
        "search.sign_in": "Ingresar",
        "brand.tagline": "Empleos por día en EE. UU. y Canadá — a un toque 📍",
        "menu.admin_panel": "Panel de administración",
        "menu.my_apps": "Mis postulaciones",
        "menu.job_alerts": "Alertas de empleo",
        "menu.saved": "Empleos guardados",
        "menu.employer_portal": "Portal de empleadores",
        "menu.logout": "Cerrar sesión",
        // Filter chips
        "chip.all": "Todos",
        "cat.restaurant": "Restaurante",
        "cat.retail": "Tiendas",
        "cat.warehouse": "Almacén",
        "cat.delivery": "Reparto",
        "cat.hotels": "Hoteles",
        "cat.care": "Cuidados",
        "cat.cleaning": "Limpieza",
        "cat.security": "Seguridad",
        "cat.events": "Eventos",
        // Loading / status
        "loading.jobs": "Cargando empleos",
        "loading.subline": "Buscando las últimas vacantes en EE. UU. y Canadá…",
        "loading.pill": "Cargando empleos…",
        "loading.tip": "Consejo: toca cualquier pin para postular en un clic, sin registro 👋",
        "loading.retry": "No se pudo conectar. Mostrando una muestra — intenta recargar.",
        // Onboarding cards
        "ob.card1.eyebrow": "Bienvenido a Dailyworkjobs",
        "ob.card1.title": "Empleos por hora reales — en el mapa",
        "ob.card1.body": "Miles de trabajos por día en EE. UU. y Canadá, todos en un solo lugar. Sin registro, sin muros de currículum. Solo toca y postula.",
        "ob.card1.cta": "Muéstrame",
        "ob.card2.eyebrow": "Cómo funciona",
        "ob.card2.title": "Explora → toca un pin → postula",
        "ob.card2.body": "Cada pin es un empleador real cerca de ti. Toca uno para ver pago y turno, ingresa tu nombre/teléfono/correo una sola vez y te llevamos a su página de postulación real.",
        "ob.card2.cta": "Entendido",
        "ob.card2.step1": "1. Explora el mapa",
        "ob.card2.step2": "2. Toca un pin",
        "ob.card2.step3": "3. Postula en un toque",
        "ob.card3.eyebrow": "Empleadores también bienvenidos",
        "ob.card3.title": "Publica empleos. Recibe alertas. Guarda favoritos.",
        "ob.card3.body": "Los empleadores pueden publicar directamente en el Portal de Empleadores (los pines ★ marcan empleadores verificados). Quienes buscan trabajo reciben alertas diarias y pueden guardar empleos.",
        "ob.card3.cta": "Casi listo",
        "ob.card3.employers": "Empleadores",
        "ob.card3.jobseekers": "Buscadores",
        "ob.card3.emp_perks": "Publica · Verifícate · ★ Destacado",
        "ob.card3.js_perks": "Alertas diarias · Guarda · Sigue postulaciones",
        "ob.card4.eyebrow": "Todo listo",
        "ob.card4.title": "Encontremos tu próximo turno",
        "ob.card4.body": "El mapa está listo y lleno de empleos en vivo. Haz zoom a tu ciudad, filtra por categoría y postula a todos los que quieras — es totalmente gratis.",
        "ob.card4.cta": "Comencemos",
        "ob.card4.strap": "Gratis · Sin registro · Sin currículum",
        "ob.progress": "de",
        // Language toggle
        "lang.toggle.title": "Change language / Cambiar idioma",
    },
};

const _listeners = new Set();
let _current = null;

function _read() {
    if (_current) return _current;
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved && SUPPORTED.includes(saved)) {
            _current = saved;
            return _current;
        }
        // Auto-detect Spanish only if no preference stored.
        const nav = (typeof navigator !== "undefined" && navigator.language) || "";
        if (nav.toLowerCase().startsWith("es")) {
            _current = "es";
            return _current;
        }
    } catch { /* ignore */ }
    _current = DEFAULT;
    return _current;
}

export function getLang() {
    return _read();
}

export function setLang(lang) {
    if (!SUPPORTED.includes(lang)) return;
    _current = lang;
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
    // Notify all subscribed components.
    _listeners.forEach((fn) => {
        try { fn(lang); } catch { /* ignore */ }
    });
    try {
        document.documentElement.setAttribute("lang", lang);
    } catch { /* ignore */ }
}

export function t(key) {
    const lang = _read();
    return (DICT[lang] && DICT[lang][key]) || DICT.en[key] || key;
}

// Hook: re-renders the component whenever the language changes.
export function useLang() {
    const [lang, setLangState] = useState(_read());
    useEffect(() => {
        const fn = (next) => setLangState(next);
        _listeners.add(fn);
        return () => _listeners.delete(fn);
    }, []);
    return {
        lang,
        t,
        setLang,
        toggle: () => setLang(lang === "en" ? "es" : "en"),
    };
}

// Initial html-lang attribute sync at import time.
try {
    if (typeof document !== "undefined") {
        document.documentElement.setAttribute("lang", _read());
    }
} catch { /* ignore */ }
